<template>
    <div class="student-monitoring" :class="{ dark: Boolean(dark) }">
        <div class="student-monitoring__inner">
            <!-- Cabeçalho -->
            <header class="student-monitoring__header">
                <div class="student-monitoring__header-main">
                    <nav class="student-monitoring__breadcrumb" aria-label="Breadcrumb">
                        <span>{{ $t('student.monitoring.peopleManagement') }}</span>
                        <span class="student-monitoring__breadcrumb-sep" aria-hidden="true">/</span>
                        <span class="student-monitoring__breadcrumb-current">{{ $t('student.monitoring.title') }}</span>
                    </nav>
                    <h1 class="student-monitoring__title">{{ $t('student.monitoring.teamTitle') }}</h1>
                    <p class="student-monitoring__subtitle">
                        {{ $t('student.monitoring.teamSubtitle') }}
                    </p>
                </div>
                <div
                    v-if="lastUpdatedAt"
                    class="student-monitoring__updated"
                >
                    <span class="student-monitoring__updated-label">{{ $t('student.monitoring.lastUpdated') }}</span>
                    <span class="student-monitoring__updated-value">{{ formatUpdatedAt(lastUpdatedAt) }}</span>
                </div>
            </header>

            <!-- Erro -->
            <div
                v-if="error && !loading"
                class="student-monitoring__error"
                role="alert"
            >
                <v-icon size="22" color="#D92D20">mdi-alert-circle-outline</v-icon>
                <div>
                    <p class="student-monitoring__error-title">{{ $t('student.monitoring.loadCollaboratorsError') }}</p>
                    <p class="student-monitoring__error-text">{{ errorMessage }}</p>
                </div>
                <v-btn
                    variant="outlined"
                    color="primary"
                    size="small"
                    class="student-monitoring__error-action"
                    @click="loadCollaborators"
                >
                    {{ $t('common.retry') }}
                </v-btn>
            </div>

            <!-- KPIs -->
            <section
                v-if="loading"
                class="student-monitoring__kpis"
                aria-busy="true"
                :aria-label="$t('student.monitoring.loadingIndicators')"
            >
                <v-skeleton-loader
                    v-for="n in 4"
                    :key="`kpi-sk-${n}`"
                    type="image"
                    class="student-monitoring__kpi-skeleton"
                    height="88"
                />
            </section>
            <section
                v-else-if="!error"
                class="student-monitoring__kpis"
                :aria-label="$t('student.monitoring.managementSummary')"
            >
                <article class="student-monitoring__kpi">
                    <div class="student-monitoring__kpi-body">
                        <span class="student-monitoring__kpi-label">{{ $t('student.monitoring.collaborators') }}</span>
                        <strong class="student-monitoring__kpi-value">{{ totalCollaborators }}</strong>
                    </div>
                    <v-icon class="student-monitoring__kpi-icon" size="28">mdi-account-group-outline</v-icon>
                </article>
                <article class="student-monitoring__kpi">
                    <div class="student-monitoring__kpi-body">
                        <span class="student-monitoring__kpi-label">{{ $t('student.monitoring.averageProgress') }}</span>
                        <strong class="student-monitoring__kpi-value">{{ averageProgress }}%</strong>
                    </div>
                    <v-icon class="student-monitoring__kpi-icon" size="28">mdi-chart-line</v-icon>
                </article>
                <article class="student-monitoring__kpi">
                    <div class="student-monitoring__kpi-body">
                        <span class="student-monitoring__kpi-label">{{ $t('student.monitoring.completedCourses') }}</span>
                        <strong class="student-monitoring__kpi-value">{{ totalCompleted }}</strong>
                    </div>
                    <v-icon class="student-monitoring__kpi-icon" size="28">mdi-check-circle-outline</v-icon>
                </article>
                <article
                    class="student-monitoring__kpi"
                    :class="{ 'is-alert': totalOverdue > 0 }"
                >
                    <div class="student-monitoring__kpi-body">
                        <span class="student-monitoring__kpi-label">{{ $t('student.monitoring.overdueCourses') }}</span>
                        <strong class="student-monitoring__kpi-value">{{ totalOverdue }}</strong>
                    </div>
                    <v-icon
                        class="student-monitoring__kpi-icon"
                        size="28"
                        :color="totalOverdue > 0 ? '#D92D20' : '#98A2B3'"
                    >
                        mdi-alert-circle-outline
                    </v-icon>
                </article>
            </section>

            <!-- Filtros -->
            <section
                v-if="!error"
                class="student-monitoring__filters-panel"
                :aria-label="$t('student.monitoring.teamFilters')"
            >
                <div class="student-monitoring__filters-toolbar">
                    <h2 class="student-monitoring__filters-title">{{ $t('student.monitoring.teamFilters') }}</h2>
                    <button
                        v-if="!isDesktop"
                        type="button"
                        class="student-monitoring__filters-toggle"
                        :aria-expanded="filtersOpen"
                        aria-controls="monitoring-filters-body"
                        @click="filtersOpen = !filtersOpen"
                    >
                        {{ filtersOpen ? $t('student.monitoring.hideFilters') : $t('student.monitoring.filters') }}
                        <v-icon size="18" aria-hidden="true">
                            {{ filtersOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                        </v-icon>
                        <span
                            v-if="hasActiveFilters && !filtersOpen"
                            class="student-monitoring__filters-badge"
                        >
                            {{ $t('student.monitoring.activeFilters') }}
                        </span>
                    </button>
                </div>

                <div
                    v-show="showFiltersBody"
                    id="monitoring-filters-body"
                    class="student-monitoring__filters-body"
                >
                    <v-text-field
                        v-model="filters.search"
                        :label="$t('student.monitoring.searchCollaborator')"
                        :placeholder="$t('student.monitoring.searchCollaboratorPlaceholder')"
                        variant="outlined"
                        density="compact"
                        hide-details
                        clearable
                        prepend-inner-icon="mdi-magnify"
                        class="student-monitoring__filter student-monitoring__filter--search"
                        autocomplete="off"
                    />
                    <div class="student-monitoring__filters-grid">
                        <v-select
                            v-model="filters.sectorId"
                            :items="sectorOptions"
                            item-title="title"
                            item-value="value"
                            :label="$t('student.monitoring.sector')"
                            variant="outlined"
                            density="compact"
                            hide-details
                            clearable
                            class="student-monitoring__filter"
                        />
                        <v-select
                            v-model="filters.courseId"
                            :items="courseOptions"
                            item-title="title"
                            item-value="value"
                            :label="$t('student.monitoring.course')"
                            variant="outlined"
                            density="compact"
                            hide-details
                            clearable
                            class="student-monitoring__filter"
                        />
                        <v-select
                            v-model="filters.situation"
                            :items="situationOptions"
                            item-title="title"
                            item-value="value"
                            :label="$t('student.monitoring.situation')"
                            variant="outlined"
                            density="compact"
                            hide-details
                            class="student-monitoring__filter"
                        />
                        <div class="student-monitoring__filters-actions">
                            <v-btn
                                variant="outlined"
                                color="primary"
                                class="student-monitoring__clear-btn"
                                :disabled="!hasActiveFilters"
                                @click="clearFilters"
                            >
                                {{ $t('student.monitoring.clearFilters') }}
                            </v-btn>
                        </div>
                    </div>
                </div>
                <p
                    v-if="!loading"
                    class="student-monitoring__results-count"
                    aria-live="polite"
                >
                    {{ resultsCountLabel }}
                </p>
            </section>

            <!-- Loading list -->
            <div
                v-if="loading"
                class="student-monitoring__list-loading"
                aria-busy="true"
                :aria-label="$t('student.monitoring.loadingCollaborators')"
            >
                <v-skeleton-loader
                    v-for="n in 5"
                    :key="`row-sk-${n}`"
                    type="list-item-avatar-two-line"
                    class="student-monitoring__row-skeleton"
                />
            </div>

            <!-- Empty / list -->
            <template v-else-if="!error">
                <div
                    v-if="!filteredRows.length"
                    class="student-monitoring__empty"
                >
                    <v-icon size="40" color="#98A2B3">
                        {{ rows.length ? 'mdi-filter-off-outline' : 'mdi-account-group-outline' }}
                    </v-icon>
                    <h3 class="student-monitoring__empty-title">
                        {{
                            rows.length
                                ? $t('student.monitoring.noCollaboratorFound')
                                : $t('student.monitoring.noCollaboratorAvailable')
                        }}
                    </h3>
                    <p class="student-monitoring__empty-text">
                        {{
                            rows.length
                                ? $t('student.monitoring.reviewFilters')
                                : $t('student.monitoring.noCollaboratorsLinked')
                        }}
                    </p>
                    <v-btn
                        v-if="rows.length && hasActiveFilters"
                        variant="outlined"
                        color="primary"
                        @click="clearFilters"
                    >
                        {{ $t('student.monitoring.clearFilters') }}
                    </v-btn>
                </div>

                <template v-else>
                    <!-- Desktop table -->
                    <div
                        v-if="isDesktop"
                        class="student-monitoring__table-wrap"
                    >
                        <table class="student-monitoring__table">
                            <thead>
                                <tr>
                                    <th scope="col">{{ $t('student.monitoring.tableCollaborator') }}</th>
                                    <th scope="col">{{ $t('student.monitoring.tableSectorRole') }}</th>
                                    <th scope="col">{{ $t('student.monitoring.tableProgress') }}</th>
                                    <th scope="col" class="is-numeric">{{ $t('student.monitoring.tableCompleted') }}</th>
                                    <th scope="col" class="is-numeric">{{ $t('student.monitoring.tableInProgress') }}</th>
                                    <th scope="col" class="is-numeric">{{ $t('student.monitoring.tableOverdue') }}</th>
                                    <th scope="col">{{ $t('student.monitoring.tableSituation') }}</th>
                                    <th scope="col" class="is-action">
                                        <span class="visually-hidden">{{ $t('student.monitoring.tableAction') }}</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="row in filteredRows"
                                    :key="row.id"
                                >
                                    <td>
                                        <div class="student-monitoring__identity">
                                            <span
                                                class="student-monitoring__avatar"
                                                aria-hidden="true"
                                            >
                                                {{ initials(row.name) }}
                                            </span>
                                            <strong
                                                class="student-monitoring__name"
                                                :title="isNameLong(row.name) ? row.name : undefined"
                                            >
                                                {{ row.name }}
                                            </strong>
                                        </div>
                                    </td>
                                    <td>
                                        <div class="student-monitoring__sector-cell">
                                            <span class="student-monitoring__sector">
                                                {{ row.sectorName || $t('student.monitoring.noSector') }}
                                            </span>
                                            <span
                                                v-if="row.roleName"
                                                class="student-monitoring__role"
                                            >
                                                {{ row.roleName }}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div class="student-monitoring__progress-cell">
                                            <span class="student-monitoring__progress-pct">
                                                {{ row.progressPercent || 0 }}%
                                            </span>
                                            <div
                                                class="student-monitoring__progress-track"
                                                role="progressbar"
                                                :aria-valuenow="row.progressPercent || 0"
                                                aria-valuemin="0"
                                                aria-valuemax="100"
                                                :aria-label="$t('student.monitoring.progressOf', { name: row.name })"
                                            >
                                                <div
                                                    class="student-monitoring__progress-fill"
                                                    :class="getProgressBarClass(row)"
                                                    :style="{ width: `${Math.min(100, row.progressPercent || 0)}%` }"
                                                />
                                            </div>
                                        </div>
                                    </td>
                                    <td class="is-numeric">{{ row.completed }}</td>
                                    <td class="is-numeric">{{ row.inProgress }}</td>
                                    <td
                                        class="is-numeric"
                                        :class="{ 'is-alert': row.overdue > 0 }"
                                    >
                                        {{ row.overdue }}
                                    </td>
                                    <td>
                                        <span
                                            class="student-monitoring__chip"
                                            :class="`is-${getSituation(row)}`"
                                        >
                                            {{ getSituationLabel(row) }}
                                        </span>
                                    </td>
                                    <td class="is-action">
                                        <v-btn
                                            icon
                                            variant="text"
                                            size="small"
                                            color="primary"
                                            :title="$t('student.monitoring.viewDetails')"
                                            :aria-label="$t('student.monitoring.viewDetailsOf', { name: row.name })"
                                            @click="openDetail(row.id)"
                                        >
                                            <v-icon size="22">mdi-chevron-right</v-icon>
                                        </v-btn>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Mobile cards -->
                    <ul
                        v-else
                        class="student-monitoring__cards"
                    >
                        <li
                            v-for="row in filteredRows"
                            :key="row.id"
                            class="student-monitoring__card"
                        >
                            <div class="student-monitoring__card-header">
                                <div class="student-monitoring__identity">
                                    <span
                                        class="student-monitoring__avatar"
                                        aria-hidden="true"
                                    >
                                        {{ initials(row.name) }}
                                    </span>
                                    <div class="student-monitoring__card-identity-text">
                                        <strong class="student-monitoring__name">{{ row.name }}</strong>
                                        <span
                                            v-if="row.roleName"
                                            class="student-monitoring__role"
                                        >
                                            {{ row.roleName }}
                                        </span>
                                    </div>
                                </div>
                                <span
                                    class="student-monitoring__chip"
                                    :class="`is-${getSituation(row)}`"
                                >
                                    {{ getSituationLabel(row) }}
                                </span>
                            </div>

                            <div class="student-monitoring__card-progress">
                                <div class="student-monitoring__card-progress-meta">
                                    <span>{{ $t('student.monitoring.tableProgress') }}</span>
                                    <strong>{{ row.progressPercent || 0 }}%</strong>
                                </div>
                                <div
                                    class="student-monitoring__progress-track"
                                    role="progressbar"
                                    :aria-valuenow="row.progressPercent || 0"
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                    :aria-label="$t('student.monitoring.progressOf', { name: row.name })"
                                >
                                    <div
                                        class="student-monitoring__progress-fill"
                                        :class="getProgressBarClass(row)"
                                        :style="{ width: `${Math.min(100, row.progressPercent || 0)}%` }"
                                    />
                                </div>
                            </div>

                            <div class="student-monitoring__mobile-stats">
                                <div class="student-monitoring__mobile-stat">
                                    <span>{{ $t('student.monitoring.tableCompleted') }}</span>
                                    <strong>{{ row.completed }}</strong>
                                </div>
                                <div class="student-monitoring__mobile-stat">
                                    <span>{{ $t('student.monitoring.tableInProgress') }}</span>
                                    <strong>{{ row.inProgress }}</strong>
                                </div>
                                <div class="student-monitoring__mobile-stat">
                                    <span>{{ $t('student.monitoring.tableOverdue') }}</span>
                                    <strong :class="{ 'is-alert': row.overdue > 0 }">
                                        {{ row.overdue }}
                                    </strong>
                                </div>
                            </div>

                            <div class="student-monitoring__mobile-sector">
                                <v-icon size="16" aria-hidden="true">mdi-domain</v-icon>
                                <span>{{ row.sectorName || $t('student.monitoring.noSector') }}</span>
                            </div>

                            <button
                                type="button"
                                class="student-monitoring__detail-btn"
                                :aria-label="$t('student.monitoring.viewDetailsOf', { name: row.name })"
                                @click="openDetail(row.id)"
                            >
                                {{ $t('student.monitoring.viewDetails') }}
                                <v-icon size="18" aria-hidden="true">mdi-chevron-right</v-icon>
                            </button>
                        </li>
                    </ul>
                </template>
            </template>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import corporateUsersApi, { canAccessTeamMonitoring } from '@/utils/corporate/users';
import { getPreviewQueryParams } from '@/utils/corporate/portalViewer';
import { intlLocale } from '@/i18n/locale';

export default {
    name: 'StudentMonitoring',
    data() {
        return {
            loading: true,
            error: false,
            errorMessage: '',
            rows: [],
            filtersOpen: false,
            filters: {
                search: '',
                sectorId: null,
                courseId: null,
                situation: 'all',
            },
        };
    },
    computed: {
        ...mapState(['token', 'dark', 'user', 'previewAs']),
        canAccessMonitoring() {
            return canAccessTeamMonitoring(this.user);
        },
        isDesktop() {
            return this.$vuetify?.display?.mdAndUp === true;
        },
        showFiltersBody() {
            return this.isDesktop || this.filtersOpen;
        },
        situationOptions() {
            return [
                { value: 'all', title: this.$t('common.all') },
                { value: 'in_progress', title: this.$t('student.monitoring.situationInProgress') },
                { value: 'completed', title: this.$t('student.monitoring.situationCompleted') },
                { value: 'attention', title: this.$t('student.monitoring.situationAttention') },
                { value: 'overdue', title: this.$t('student.monitoring.situationOverdue') },
            ];
        },
        sectorOptions() {
            const map = new Map();
            this.rows.forEach((row) => {
                if (row.sectorId && row.sectorName) {
                    map.set(row.sectorId, row.sectorName);
                }
            });
            return Array.from(map.entries()).map(([value, title]) => ({ value, title }));
        },
        courseOptions() {
            const map = new Map();
            this.rows.forEach((row) => {
                (row.enrolments || []).forEach((course) => {
                    if (course.courseId && course.courseName) {
                        map.set(course.courseId, course.courseName);
                    }
                });
            });
            return Array.from(map.entries()).map(([value, title]) => ({ value, title }));
        },
        filteredRows() {
            const search = String(this.filters.search || '').trim().toLowerCase();
            return this.rows.filter((row) => {
                if (search) {
                    const name = String(row.name || '').toLowerCase();
                    if (!name.includes(search)) return false;
                }
                if (this.filters.sectorId && row.sectorId !== this.filters.sectorId) {
                    return false;
                }
                if (this.filters.courseId) {
                    const hasCourse = (row.enrolments || []).some(
                        (course) => course.courseId === this.filters.courseId,
                    );
                    if (!hasCourse) return false;
                }
                if (this.filters.situation && this.filters.situation !== 'all') {
                    if (this.getSituation(row) !== this.filters.situation) return false;
                }
                return true;
            });
        },
        totalCollaborators() {
            return this.filteredRows.length;
        },
        averageProgress() {
            const list = this.filteredRows;
            if (!list.length) return 0;
            const sum = list.reduce((acc, row) => acc + (Number(row.progressPercent) || 0), 0);
            return Math.round(sum / list.length);
        },
        totalCompleted() {
            return this.filteredRows.reduce((acc, row) => acc + (Number(row.completed) || 0), 0);
        },
        totalOverdue() {
            return this.filteredRows.reduce((acc, row) => acc + (Number(row.overdue) || 0), 0);
        },
        hasActiveFilters() {
            const { search, sectorId, courseId, situation } = this.filters;
            return Boolean(
                (search && String(search).trim())
                || sectorId
                || courseId
                || (situation && situation !== 'all'),
            );
        },
        resultsCountLabel() {
            const n = this.filteredRows.length;
            if (n === 1) return this.$t('student.monitoring.resultsCountOne');
            return this.$t('student.monitoring.resultsCountMany', { n });
        },
        lastUpdatedAt() {
            let latest = null;
            this.rows.forEach((row) => {
                if (!row.updatedAt) return;
                const ts = new Date(row.updatedAt).getTime();
                if (Number.isNaN(ts)) return;
                if (latest === null || ts > latest) latest = ts;
            });
            return latest;
        },
    },
    watch: {
        canAccessMonitoring: {
            immediate: true,
            handler(value) {
                if (!value) {
                    this.$router.replace('/aluno/dashboard');
                }
            },
        },
        isDesktop: {
            immediate: true,
            handler(desktop) {
                if (desktop) {
                    this.filtersOpen = true;
                } else if (!this.hasActiveFilters) {
                    this.filtersOpen = false;
                }
            },
        },
        previewAs: {
            deep: true,
            handler() {
                if (this.canAccessMonitoring) {
                    this.loadCollaborators();
                }
            },
        },
    },
    created() {
        if (!this.canAccessMonitoring) {
            this.$router.replace('/aluno/dashboard');
            return;
        }
        this.loadCollaborators();
    },
    methods: {
        initials(name = '') {
            const parts = String(name).trim().split(/\s+/).filter(Boolean);
            if (parts.length >= 2) {
                return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
            }
            return (parts[0] || '?').slice(0, 2).toUpperCase();
        },
        isNameLong(name = '') {
            return String(name).length > 28;
        },
        getSituation(row) {
            const overdue = Number(row?.overdue) || 0;
            const progress = Number(row?.progressPercent) || 0;
            if (overdue > 0) return 'overdue';
            if (progress >= 100) return 'completed';
            if (progress < 30) return 'attention';
            return 'in_progress';
        },
        getSituationLabel(row) {
            const labels = {
                overdue: this.$t('student.monitoring.situationOverdue'),
                attention: this.$t('student.monitoring.situationAttention'),
                in_progress: this.$t('student.monitoring.situationInProgress'),
                completed: this.$t('student.monitoring.situationCompleted'),
            };
            return labels[this.getSituation(row)] || labels.in_progress;
        },
        getProgressBarClass(row) {
            return `is-${this.getSituation(row)}`;
        },
        clearFilters() {
            this.filters = {
                search: '',
                sectorId: null,
                courseId: null,
                situation: 'all',
            };
        },
        formatUpdatedAt(timestamp) {
            if (!timestamp) return '';
            try {
                return new Intl.DateTimeFormat(intlLocale(this.$i18n.locale), {
                    dateStyle: 'short',
                    timeStyle: 'short',
                }).format(new Date(timestamp));
            } catch (e) {
                return '';
            }
        },
        openDetail(id) {
            this.$router.push(`/aluno/acompanhamento/${id}`);
        },
        summarize(user) {
            const enrolments = user.enrolments || [];
            return {
                ...user,
                completed: user.completed != null
                    ? Number(user.completed) || 0
                    : enrolments.filter((e) => e.status === 'completed').length,
                inProgress: user.inProgress != null
                    ? Number(user.inProgress) || 0
                    : enrolments.filter((e) => e.status === 'in_progress').length,
                overdue: user.overdue != null
                    ? Number(user.overdue) || 0
                    : enrolments.filter((e) => e.status === 'overdue').length,
            };
        },
        async loadCollaborators() {
            this.loading = true;
            this.error = false;
            this.errorMessage = '';
            try {
                const response = await corporateUsersApi.teamMonitoring(this.token, {
                    size: 200,
                    ...getPreviewQueryParams(),
                });
                const list = response?.content || [];
                this.rows = list.map((user) => this.summarize(user));
            } catch (e) {
                this.rows = [];
                this.error = true;
                const status = e?.response?.status;
                if (status === 403) {
                    this.errorMessage = this.$t('student.monitoring.noPermissionTeam');
                } else {
                    this.errorMessage = this.$t('student.monitoring.fetchError');
                }
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.student-monitoring {
    --sm-bg: #F5F7FA;
    --sm-surface: #FFFFFF;
    --sm-text: #1F2937;
    --sm-text-secondary: #667085;
    --sm-border: #E4E7EC;
    --sm-alert: #D92D20;
    --sm-success: #039855;
    --sm-warning: #DC6803;
    --sm-progress: #1ac4cd;
    --sm-radius: 10px;

    min-height: 60vh;
    padding: 1.5rem 1rem 3rem;
    background: var(--sm-bg);
    color: var(--sm-text);
}

.student-monitoring.dark {
    --sm-bg: #12141a;
    --sm-surface: #1c1f28;
    --sm-text: #f2f4f8;
    --sm-text-secondary: #9aa3b2;
    --sm-border: #2a3040;
    --sm-progress: #5ad4db;
    background: var(--sm-bg);
}

.student-monitoring__inner {
    max-width: 1200px;
    margin: 0 auto;
}

/* Header */
.student-monitoring__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1.25rem;
    margin-bottom: 1.5rem;
}

.student-monitoring__breadcrumb {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.35rem;
    margin-bottom: 0.5rem;
    font-size: 0.8125rem;
    color: var(--sm-text-secondary);
}

.student-monitoring__breadcrumb-sep {
    opacity: 0.7;
}

.student-monitoring__breadcrumb-current {
    color: var(--sm-text);
    font-weight: 500;
}

.student-monitoring__title {
    margin: 0 0 0.4rem;
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.25;
    color: var(--sm-text);
    text-align: left;
}

.student-monitoring__subtitle {
    margin: 0;
    max-width: 42rem;
    font-size: 0.9375rem;
    line-height: 1.5;
    color: var(--sm-text-secondary);
    text-align: left;
}

.student-monitoring__updated {
    flex-shrink: 0;
    text-align: right;
    padding-top: 0.15rem;
}

.student-monitoring__updated-label {
    display: block;
    font-size: 0.75rem;
    color: var(--sm-text-secondary);
    margin-bottom: 0.15rem;
}

.student-monitoring__updated-value {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--sm-text);
}

/* Error */
.student-monitoring__error {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.85rem;
    margin-bottom: 1.25rem;
    padding: 1rem 1.15rem;
    background: var(--sm-surface);
    border: 1px solid var(--sm-border);
    border-left: 3px solid var(--sm-alert);
    border-radius: var(--sm-radius);
}

.student-monitoring__error-title {
    margin: 0 0 0.2rem;
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--sm-text);
}

.student-monitoring__error-text {
    margin: 0;
    font-size: 0.875rem;
    color: var(--sm-text-secondary);
}

.student-monitoring__error-action {
    margin-left: auto;
}

/* KPIs */
.student-monitoring__kpis {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.85rem;
    margin-bottom: 1.25rem;
}

.student-monitoring__kpi,
.student-monitoring__kpi-skeleton {
    background: var(--sm-surface);
    border: 1px solid var(--sm-border);
    border-radius: var(--sm-radius);
    min-height: 96px;
}

@media (min-width: 960px) {
    .student-monitoring__kpi-skeleton :deep(.v-skeleton-loader__image) {
        height: 96px;
    }
}

.student-monitoring__kpi {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 1rem 1.1rem;
}

.student-monitoring__kpi.is-alert {
    border-color: #fda29b;
    background: #fffbfa;
}

.student-monitoring.dark .student-monitoring__kpi.is-alert {
    background: #2a1c1c;
    border-color: #7a3b3b;
}

.student-monitoring__kpi-label {
    display: block;
    font-size: 0.75rem;
    font-weight: 500;
    color: var(--sm-text-secondary);
    margin-bottom: 0.35rem;
}

.student-monitoring__kpi-value {
    display: block;
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.1;
    color: var(--sm-text);
}

.student-monitoring__kpi.is-alert .student-monitoring__kpi-value {
    color: var(--sm-alert);
}

.student-monitoring__kpi-icon {
    color: #98A2B3 !important;
    flex-shrink: 0;
}

.student-monitoring__kpi-skeleton :deep(.v-skeleton-loader__image) {
    height: 96px;
    border-radius: var(--sm-radius);
}

/* Filters */
.student-monitoring__filters-panel {
    margin-bottom: 1rem;
    padding: 1.15rem 1.2rem 1rem;
    background: var(--sm-surface);
    border: 1px solid var(--sm-border);
    border-radius: var(--sm-radius);
}

.student-monitoring__filters-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.85rem;
}

.student-monitoring__filters-title {
    margin: 0;
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--sm-text);
}

.student-monitoring__filters-toggle {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    min-height: 44px;
    padding: 0.35rem 0.5rem;
    border: 1px solid var(--sm-border);
    border-radius: 8px;
    background: var(--sm-surface);
    color: var(--sm-text);
    font-size: 0.8125rem;
    font-weight: 600;
    cursor: pointer;
    flex-shrink: 0;
}

.student-monitoring__filters-toggle:focus-visible {
    outline: 2px solid var(--sm-progress);
    outline-offset: 2px;
}

.student-monitoring__filters-badge {
    display: inline-flex;
    align-items: center;
    margin-left: 0.15rem;
    padding: 0.1rem 0.4rem;
    border-radius: 999px;
    background: #e8f8f9;
    color: #0f8f96;
    font-size: 0.6875rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.02em;
}

.student-monitoring.dark .student-monitoring__filters-badge {
    background: #1e3336;
    color: #5ad4db;
}

.student-monitoring__filters-body {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.student-monitoring__filter--search {
    margin-bottom: 0;
}

.student-monitoring__filters-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr auto;
    gap: 0.75rem;
    align-items: end;
}

.student-monitoring__filters-actions {
    display: flex;
    align-items: flex-end;
}

.student-monitoring__clear-btn {
    white-space: nowrap;
}

.student-monitoring__results-count {
    margin: 0.85rem 0 0;
    font-size: 0.8125rem;
    color: var(--sm-text-secondary);
}

/* Loading rows */
.student-monitoring__list-loading {
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
}

.student-monitoring__row-skeleton {
    background: var(--sm-surface);
    border: 1px solid var(--sm-border);
    border-radius: var(--sm-radius);
    overflow: hidden;
}

/* Empty */
.student-monitoring__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    min-height: 240px;
    padding: 2rem 1.5rem;
    text-align: center;
    background: var(--sm-surface);
    border: 1px solid var(--sm-border);
    border-radius: var(--sm-radius);
}

.student-monitoring__empty-title {
    margin: 0.35rem 0 0;
    font-size: 1.0625rem;
    font-weight: 600;
    color: var(--sm-text);
}

.student-monitoring__empty-text {
    margin: 0 0 0.75rem;
    max-width: 26rem;
    font-size: 0.875rem;
    color: var(--sm-text-secondary);
}

/* Table */
.student-monitoring__table-wrap {
    background: var(--sm-surface);
    border: 1px solid var(--sm-border);
    border-radius: var(--sm-radius);
    overflow-x: auto;
}

.student-monitoring__table {
    width: 100%;
    border-collapse: collapse;
    min-width: 860px;
}

.student-monitoring__table th,
.student-monitoring__table td {
    padding: 0.85rem 1rem;
    text-align: left;
    vertical-align: middle;
    border-bottom: 1px solid var(--sm-border);
}

.student-monitoring__table th {
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    color: var(--sm-text-secondary);
    background: #FAFBFC;
    white-space: nowrap;
}

.student-monitoring.dark .student-monitoring__table th {
    background: #171a22;
}

.student-monitoring__table tbody tr {
    transition: background-color 0.12s ease;
}

.student-monitoring__table tbody tr:hover {
    background: rgba(26, 196, 205, 0.04);
}

.student-monitoring__table tbody tr:last-child td {
    border-bottom: 0;
}

.student-monitoring__table .is-numeric {
    text-align: center;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    color: var(--sm-text);
}

.student-monitoring__table .is-numeric.is-alert {
    color: var(--sm-alert);
}

.student-monitoring__table .is-action {
    width: 56px;
    text-align: center;
}

/* Identity */
.student-monitoring__identity {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-width: 0;
}

.student-monitoring__avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #e8f8f9;
    color: #0f8f96;
    font-weight: 700;
    font-size: 0.8rem;
    flex-shrink: 0;
}

.student-monitoring.dark .student-monitoring__avatar {
    background: #24343a;
    color: #5ad4db;
}

.student-monitoring__name {
    display: block;
    min-width: 0;
    max-width: 180px;
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--sm-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.student-monitoring__sector-cell {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    min-width: 0;
    max-width: 180px;
}

.student-monitoring__sector {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--sm-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.student-monitoring__role {
    font-size: 0.8125rem;
    color: var(--sm-text-secondary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Progress */
.student-monitoring__progress-cell {
    min-width: 110px;
    max-width: 140px;
}

.student-monitoring__progress-pct {
    display: block;
    margin-bottom: 0.35rem;
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--sm-text);
}

.student-monitoring__progress-track {
    height: 6px;
    border-radius: 999px;
    background: #eef1f6;
    overflow: hidden;
}

.student-monitoring.dark .student-monitoring__progress-track {
    background: #2a3040;
}

.student-monitoring__progress-fill {
    height: 100%;
    border-radius: inherit;
    transition: width 0.2s ease;
}

.student-monitoring__progress-fill.is-overdue {
    background: var(--sm-alert);
}

.student-monitoring__progress-fill.is-attention {
    background: var(--sm-warning);
}

.student-monitoring__progress-fill.is-in_progress {
    background: var(--sm-progress);
}

.student-monitoring__progress-fill.is-completed {
    background: var(--sm-success);
}

/* Chips */
.student-monitoring__chip {
    display: inline-flex;
    align-items: center;
    padding: 0.2rem 0.55rem;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1.3;
    white-space: nowrap;
}

.student-monitoring__card .student-monitoring__chip {
    flex-shrink: 0;
    padding: 0.2rem 0.55rem;
    border-radius: 999px;
    font-size: 0.6875rem;
}

.student-monitoring__chip.is-overdue {
    color: var(--sm-alert);
    background: #fef3f2;
}

.student-monitoring__chip.is-attention {
    color: var(--sm-warning);
    background: #fffaeb;
}

.student-monitoring__chip.is-in_progress {
    color: #0f8f96;
    background: #e8f8f9;
}

.student-monitoring__chip.is-completed {
    color: var(--sm-success);
    background: #ecfdf3;
}

.student-monitoring.dark .student-monitoring__chip.is-overdue {
    background: #3a2222;
}

.student-monitoring.dark .student-monitoring__chip.is-attention {
    background: #3a2e18;
}

.student-monitoring.dark .student-monitoring__chip.is-in_progress {
    background: #1e3336;
}

.student-monitoring.dark .student-monitoring__chip.is-completed {
    background: #1c3228;
}

/* Mobile cards */
.student-monitoring__cards {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.student-monitoring__card {
    width: 100%;
    background: var(--sm-surface);
    border: 1px solid var(--sm-border);
    border-radius: 12px;
    padding: 16px;
}

.student-monitoring__card-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
}

.student-monitoring__card .student-monitoring__identity {
    flex: 1;
    min-width: 0;
    gap: 0.7rem;
}

.student-monitoring__card-identity-text {
    min-width: 0;
    text-align: left;
}

.student-monitoring__card .student-monitoring__avatar {
    width: 38px;
    height: 38px;
    font-size: 0.72rem;
    font-weight: 600;
}

.student-monitoring__card .student-monitoring__name {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    max-width: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
    word-break: break-word;
    font-size: 0.9375rem;
    font-weight: 700;
    line-height: 1.35;
    color: var(--sm-text);
    text-align: left;
}

.student-monitoring__card .student-monitoring__role {
    display: block;
    margin-top: 2px;
    font-size: 0.8125rem;
    line-height: 1.35;
    color: var(--sm-text-secondary);
    text-align: left;
    white-space: normal;
}

.student-monitoring__card-progress {
    width: 100%;
    margin-top: 16px;
}

.student-monitoring__card-progress-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 7px;
    font-size: 0.8125rem;
    color: var(--sm-text-secondary);
}

.student-monitoring__card-progress-meta strong {
    font-size: 0.875rem;
    font-weight: 700;
    color: var(--sm-text);
}

.student-monitoring__card .student-monitoring__progress-track {
    width: 100%;
    height: 6px;
    background: var(--sm-border);
}

.student-monitoring__mobile-stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    margin-top: 16px;
    padding: 13px 0;
    border-top: 1px solid #EAECF0;
    border-bottom: 1px solid #EAECF0;
}

.student-monitoring.dark .student-monitoring__mobile-stats {
    border-color: var(--sm-border);
}

.student-monitoring__mobile-stat {
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 0 0.35rem;
}

.student-monitoring__mobile-stat:not(:last-child) {
    border-right: 1px solid #EAECF0;
}

.student-monitoring.dark .student-monitoring__mobile-stat:not(:last-child) {
    border-right-color: var(--sm-border);
}

.student-monitoring__mobile-stat span {
    font-size: 0.625rem;
    font-weight: 500;
    line-height: 1.25;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: var(--sm-text-secondary);
}

.student-monitoring__mobile-stat strong {
    margin-top: 5px;
    font-size: 1.0625rem;
    font-weight: 700;
    line-height: 1.2;
    color: var(--sm-text);
    font-variant-numeric: tabular-nums;
}

.student-monitoring__mobile-stat strong.is-alert {
    color: var(--sm-alert);
}

.student-monitoring__mobile-sector {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin-top: 13px;
    min-width: 0;
    font-size: 0.8125rem;
    line-height: 1.35;
    color: var(--sm-text-secondary);
    text-align: left;
}

.student-monitoring__mobile-sector span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.student-monitoring__detail-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.2rem;
    width: 100%;
    min-height: 42px;
    margin-top: 14px;
    padding: 0.5rem 0.75rem;
    border: 1px solid rgba(26, 196, 205, 0.45);
    border-radius: 8px;
    background: transparent;
    color: #0f8f96;
    font-size: 0.8125rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;
}

.student-monitoring.dark .student-monitoring__detail-btn {
    color: #5ad4db;
    border-color: rgba(90, 212, 219, 0.45);
}

.student-monitoring__detail-btn:hover,
.student-monitoring__detail-btn:focus-visible {
    background: rgba(26, 196, 205, 0.08);
    border-color: #1ac4cd;
}

.student-monitoring__detail-btn:focus-visible {
    outline: 2px solid var(--sm-progress);
    outline-offset: 2px;
}

/* A11y */
.visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.student-monitoring :deep(.v-btn:focus-visible) {
    outline: 2px solid var(--sm-progress);
    outline-offset: 2px;
}

.student-monitoring :deep(.v-field:focus-within) {
    outline: none;
}

/* Responsive — mobile (alinhado ao dashboard) */
@media (max-width: 959px) {
    .student-monitoring {
        padding: 12px 16px 24px;
        overflow-x: hidden;
    }

    .student-monitoring__header {
        flex-direction: column;
        align-items: stretch;
        gap: 0.5rem;
        margin-bottom: 1rem;
    }

    .student-monitoring__breadcrumb {
        font-size: 0.75rem;
    }

    .student-monitoring__title {
        font-size: 1.25rem;
        line-height: 1.25;
    }

    .student-monitoring__subtitle {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        overflow: hidden;
        font-size: 0.8125rem;
        line-height: 1.4;
    }

    .student-monitoring__updated {
        align-self: flex-start;
        text-align: left;
    }

    .student-monitoring__kpis {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 0.75rem;
        margin: 0 0 1rem;
        padding: 0;
        overflow: visible;
    }

    .student-monitoring__kpi,
    .student-monitoring__kpi-skeleton {
        min-height: 88px;
        height: auto;
    }

    .student-monitoring__kpi {
        padding: 1rem 1rem;
        gap: 0.65rem;
    }

    .student-monitoring__kpi-label {
        margin-bottom: 0.3rem;
        font-size: 0.75rem;
    }

    .student-monitoring__kpi-value {
        font-size: 1.35rem;
    }

    .student-monitoring__kpi-icon {
        font-size: 24px !important;
        width: 24px !important;
        height: 24px !important;
    }

    .student-monitoring__kpi-skeleton :deep(.v-skeleton-loader__image) {
        height: 88px;
    }

    .student-monitoring__filters-panel {
        padding: 0.9rem 0.95rem 0.85rem;
    }

    .student-monitoring__filters-toolbar {
        margin-bottom: 0;
    }

    .student-monitoring__filters-toolbar + .student-monitoring__results-count {
        margin-top: 0.7rem;
    }

    .student-monitoring__filters-body {
        margin-top: 0.75rem;
    }

    .student-monitoring__filters-title {
        font-size: 0.875rem;
    }

    .student-monitoring__filters-grid {
        grid-template-columns: 1fr;
    }

    .student-monitoring__filters-actions {
        grid-column: 1 / -1;
    }

    .student-monitoring__clear-btn {
        width: 100%;
        min-height: 44px;
    }

    .student-monitoring__results-count {
        margin-top: 0.7rem;
    }

    .student-monitoring__cards {
        gap: 12px;
    }

    .student-monitoring__card {
        padding: 16px 18px;
    }
}

@media (max-width: 480px) {
    .student-monitoring {
        padding: 12px 12px 24px;
    }

    .student-monitoring__kpis {
        gap: 0.65rem;
    }

    .student-monitoring__kpi {
        padding: 0.9rem 0.85rem;
    }

    .student-monitoring__title {
        font-size: 1.2rem;
    }

    .student-monitoring__card {
        padding: 16px;
    }
}

@media (max-width: 360px) {
    .student-monitoring__card-header {
        flex-wrap: wrap;
    }

    .student-monitoring__card .student-monitoring__chip {
        margin-top: 0.15rem;
    }

    .student-monitoring__mobile-stat span {
        font-size: 0.5625rem;
    }

    .student-monitoring__mobile-stat strong {
        font-size: 0.9375rem;
    }
}
</style>
